import React from 'react';
import { motion } from 'framer-motion';
import { 
  MapPin, 
  Target, 
  Heart, 
  Award, 
  Users,
  Lightbulb,
  CheckCircle
} from 'lucide-react';
import { Link } from 'react-router-dom';

const LOGO_WITHOUT_TEXT = "https://customer-assets.emergentagent.com/job_webisula-digital/artifacts/tzdi7rrr_4.png";
const OFFICE_IMAGE = "https://images.pexels.com/photos/4506936/pexels-photo-4506936.jpeg?auto=compress&cs=tinysrgb&w=800";

const values = [
  { icon: Target, title: 'Précision', desc: 'Chaque projet est réalisé avec minutie et attention aux détails' },
  { icon: Heart, title: 'Passion', desc: 'Nous aimons ce que nous faisons et ça se voit dans nos créations' },
  { icon: Award, title: 'Excellence', desc: 'Nous visons l\'excellence à chaque étape du développement' },
  { icon: Users, title: 'Proximité', desc: 'Un interlocuteur unique pour un suivi personnalisé' },
];

const stats = [
  { value: '50+', label: 'Projets réalisés' },
  { value: '100%', label: 'Clients satisfaits' },
  { value: '5+', label: 'Années d\'expérience' },
  { value: '24h', label: 'Temps de réponse' },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

export const AboutPage = () => {
  return (
    <div className="min-h-screen pt-24" data-testid="about-page">
      {/* Hero */}
      <section className="py-20 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <p className="section-label mb-4">À Propos</p>
              <h1 className="text-4xl md:text-5xl font-black tracking-tighter text-white mb-6" data-testid="about-title">
                Une agence web <span className="text-gradient">100% corse</span>
              </h1>
              <p className="text-lg text-white/60 mb-8">
                Webisula est une agence web basée à Ajaccio, spécialisée dans la création 
                de solutions digitales sur-mesure pour les entreprises corses et au-delà.
              </p>
              <p className="text-white/60 mb-8">
                Notre mission est d'accompagner les artisans, commerçants, TPE et PME dans 
                leur transformation digitale en leur proposant des outils performants, 
                modernes et adaptés à leurs besoins.
              </p>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 text-white/60">
                  <MapPin size={18} className="text-[#8B5A8B]" />
                  <span>Ajaccio, Corse</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden glow">
                <img 
                  src={OFFICE_IMAGE} 
                  alt="Notre équipe" 
                  className="w-full h-96 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0C0514] via-transparent to-transparent" />
              </div>
              {/* Logo floating */}
              <div className="absolute -bottom-8 -left-8 glass-card rounded-3xl p-6">
                <img src={LOGO_WITHOUT_TEXT} alt="Webisula" className="h-20 w-auto" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 px-6 lg:px-12 bg-[#140B1E]/50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="text-center"
                data-testid={`stat-${index}`}
              >
                <p className="text-4xl md:text-5xl font-black text-white mb-2">
                  {stat.value}
                </p>
                <p className="text-white/60">{stat.label}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="section-label mb-4">Nos Valeurs</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              Ce qui nous guide au quotidien
            </h2>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {values.map((value, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="glass-card rounded-3xl p-8 text-center"
                data-testid={`value-${index}`}
              >
                <div className="service-icon mx-auto mb-6">
                  <value.icon className="text-white" size={28} />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">
                  {value.title}
                </h3>
                <p className="text-white/60 text-sm">{value.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Why Webisula */}
      <section className="py-24 px-6 lg:px-12 bg-[#140B1E]/50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="section-label mb-4">Pourquoi Webisula ?</p>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Une expertise locale pour des projets d'envergure
              </h2>
              <ul className="space-y-4">
                {[
                  'Compréhension des besoins locaux',
                  'Technologies modernes et performantes',
                  'Accompagnement personnalisé',
                  'Prix transparents et compétitifs',
                  'Support réactif et disponible',
                  'Formation et documentation incluses'
                ].map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <CheckCircle className="text-[#8B5A8B] flex-shrink-0" size={20} />
                    <span className="text-white/80">{item}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass-card rounded-3xl p-8"
            >
              <Lightbulb className="text-[#8B5A8B] mb-6" size={40} />
              <h3 className="text-2xl font-bold text-white mb-4">
                Notre engagement
              </h3>
              <p className="text-white/60 mb-6">
                Nous nous engageons à livrer des projets de qualité, dans les délais 
                convenus, avec un accompagnement personnalisé tout au long du processus.
              </p>
              <p className="text-white/60">
                Chaque projet est unique et mérite une attention particulière. C'est 
                pourquoi nous prenons le temps de comprendre vos besoins avant de 
                proposer la solution la plus adaptée.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto glass-card rounded-3xl p-12 text-center glow"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Prêt à travailler ensemble ?
          </h2>
          <p className="text-white/60 text-lg mb-8 max-w-xl mx-auto">
            Contactez-nous pour discuter de votre projet et découvrir comment 
            nous pouvons vous aider à atteindre vos objectifs.
          </p>
          <Link
            to="/contact"
            className="glow-btn inline-block"
            data-testid="about-cta"
          >
            Démarrer un projet
          </Link>
        </motion.div>
      </section>
    </div>
  );
};

export default AboutPage;
